/**
 * B2B organization admin (canonical URL): /account-admin
 * Requires auth; after sign-in, users return here via returnUrl.
 * Signed-in: site nav + admin UI (no public marketing on this route).
 */

import { useCallback, useContext, useMemo } from 'react'
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native'
import { Redirect, useLocalSearchParams, useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { LanguageContext } from '../src/i18n'
import { SignInLoadingScreen } from '../components/signIn'
import { LandingOrgAdminNewNav, LandingHeaderControls } from '../components/landing'
import { AccountAdminScreen } from '../src/accounts/components/AccountAdminScreen/AccountAdminScreen'
import { AccountAdminSiteMenuProvider } from '../src/accounts/contexts/AccountAdminSiteMenuContext'
import { useAccountAdminDrawerShift } from '../src/accounts/contexts/AccountAdminDrawerShiftContext'
import { getWebAccountAdminDrawerPushStyle } from '../src/accounts/helpers/accountAdminDrawerPushWeb.helper'
import { appendReturnUrlQuery } from '../src/signIn/helpers/postAuthRedirect.helper'
import { PUBLIC_SIGN_IN_PATH } from '../src/signIn/constants/publicAuthRoutes.constants'
import {
  LANDING_SITE_NAV_ROUTES,
  LANDING_SITE_NAV_SCROLL_OFFSET_PX,
} from '../src/landing/constants/landingSiteNav.constants'
import { scrollWebWindowToElement } from '../src/landing/helpers/scrollWebMarketingSection.helper'
import { useAccountAdminSiteMenuLinks } from '../src/accounts/hooks/useAccountAdminSiteMenuLinks'
import {
  accountAdminSiteShellStyles,
  getAccountAdminBackToSiteChipStyles,
} from '../appStyles/_accountAdmin.styles'
import { useTheme } from '../src/theme/useTheme'
import { buildB2bOrgAccountAdminJoinPath } from '../src/accounts/helpers/b2bAccount.helper'
import { ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX } from '../src/accounts/constants/accountAdminNav.constants'
import type { AccountAdminSiteMenuLink } from '../src/accounts/types/accountAdminNav.types'

const RETURN_TARGET_BASE = '/account-admin'

interface AccountAdminPublicShellProps {
  siteMenuLinks: AccountAdminSiteMenuLink[]
  router: ReturnType<typeof useRouter>
}

function AccountAdminPublicShell({ siteMenuLinks, router }: AccountAdminPublicShellProps) {
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('AccountAdminPublicShell must be used within LanguageProvider')
  }
  const { t } = languageContext
  const { shiftPx } = useAccountAdminDrawerShift()
  const { width: windowWidth } = useWindowDimensions()
  const theme = useTheme()
  const backChipStyles = useMemo(() => getAccountAdminBackToSiteChipStyles(theme), [theme])
  const useInlineBackBar =
    Platform.OS === 'web' &&
    (windowWidth === 0 || windowWidth < ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX)

  const scheduleScrollToLandingSection = useCallback((elementId: string) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById(elementId),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [])

  const onPressHome = useCallback(() => {
    router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
  }, [router])

  const onPressBackToSite = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.replace(LANDING_SITE_NAV_ROUTES.HOME as Href)
  }, [router])

  const onPressContact = useCallback(() => {
    router.push(LANDING_SITE_NAV_ROUTES.CONTACT as Href)
  }, [router])

  const onPressFeatures = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    scheduleScrollToLandingSection('features')
  }, [router, scheduleScrollToLandingSection])

  const onPressPricing = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    scheduleScrollToLandingSection('pricing')
  }, [router, scheduleScrollToLandingSection])

  return (
    <View
      style={[accountAdminSiteShellStyles.shell, getWebAccountAdminDrawerPushStyle(shiftPx)]}
    >
      <LandingOrgAdminNewNav
        onPressHome={onPressHome}
        onPressContact={onPressContact}
        onPressFeatures={onPressFeatures}
        onPressPricing={onPressPricing}
        navMode="accountAdmin"
        headerRight={<LandingHeaderControls />}
      />
      {Platform.OS === 'web' ? (
        useInlineBackBar ? (
          <View style={backChipStyles.backInlineWrap}>
            <Pressable
              accessibilityRole="link"
              onPress={onPressBackToSite}
              style={({ hovered }) => [
                backChipStyles.backInlinePressable,
                hovered && backChipStyles.backInlinePressableHovered,
              ]}
            >
              <Text style={backChipStyles.backFloatingText}>{t.accountAdmin.backToSite}</Text>
            </Pressable>
          </View>
        ) : (
          <Pressable
            accessibilityRole="link"
            onPress={onPressBackToSite}
            style={({ hovered }) => [
              backChipStyles.backFloating,
              hovered && backChipStyles.backFloatingHovered,
            ]}
          >
            <Text style={backChipStyles.backFloatingText}>{t.accountAdmin.backToSite}</Text>
          </Pressable>
        )
      ) : null}
      <AccountAdminScreen surface="site" siteMenuLinks={siteMenuLinks} />
    </View>
  )
}

export default function AccountAdminPublicRoute() {
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const router = useRouter()
  const joinParams = useLocalSearchParams<{ join?: string | string[] }>()
  const languageContext = useContext(LanguageContext)
  const siteMenuLinks = useAccountAdminSiteMenuLinks()

  const signInReturnTarget = useMemo(() => {
    const j = joinParams.join
    const raw = typeof j === 'string' ? j : Array.isArray(j) ? j[0] : ''
    const code = raw?.trim() ?? ''
    if (!code) {
      return RETURN_TARGET_BASE
    }
    return buildB2bOrgAccountAdminJoinPath(code)
  }, [joinParams.join])

  const signInHref = useMemo(
    () => appendReturnUrlQuery(PUBLIC_SIGN_IN_PATH, signInReturnTarget) as Href,
    [signInReturnTarget],
  )

  if (!isLoaded) {
    return <SignInLoadingScreen />
  }

  if (!isSignedIn) {
    return <Redirect href={signInHref} />
  }

  if (!languageContext) {
    throw new Error('AccountAdminPublicRoute must be used within LanguageProvider')
  }

  return (
    <AccountAdminSiteMenuProvider>
      <AccountAdminPublicShell siteMenuLinks={siteMenuLinks} router={router} />
    </AccountAdminSiteMenuProvider>
  )
}
